// Small fetch wrapper. Cookies carry the session; mutating requests add the header the server
// requires to prove they came from this app.

export class ApiError extends Error {
  constructor(public status: number, message: string, public details?: any) {
    super(message);
  }
}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  const init: RequestInit = { method, credentials: "same-origin", headers: {} };
  if (method !== "GET") (init.headers as Record<string, string>)["x-taxapp"] = "1";
  if (body instanceof FormData) init.body = body;
  else if (body !== undefined) {
    (init.headers as Record<string, string>)["content-type"] = "application/json";
    init.body = JSON.stringify(body);
  }
  const response = await fetch(url, init);
  const data = response.headers.get("content-type")?.includes("application/json") ? await response.json() : null;
  if (!response.ok) {
    if (response.status === 401 && !url.startsWith("/auth/")) window.dispatchEvent(new Event("taxapp:signed-out"));
    throw new ApiError(response.status, data?.error ?? `Request failed (${response.status})`, data?.details);
  }
  return data as T;
}

export const api = {
  get: <T>(url: string) => request<T>("GET", url),
  post: <T>(url: string, body?: unknown) => request<T>("POST", url, body ?? {}),
  put: <T>(url: string, body: unknown) => request<T>("PUT", url, body),
  patch: <T>(url: string, body: unknown) => request<T>("PATCH", url, body),
  delete: <T>(url: string) => request<T>("DELETE", url),
  upload: <T>(file: File, source = "upload") => {
    const form = new FormData();
    form.append("source", source);
    form.append("file", file);
    return request<T>("POST", "/api/documents", form);
  },
  /** Stores a document as a record (not a receipt) and returns its id; a file uploaded before is reused. */
  record: async (file: File): Promise<{ documentId: string; hasText: boolean }> => {
    const form = new FormData();
    form.append("purpose", "record");
    form.append("file", file);
    try {
      return await request<{ documentId: string; hasText: boolean }>("POST", "/api/documents", form);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && e.details?.documentId) return { documentId: e.details.documentId, hasText: true };
      throw e;
    }
  },
};

// ---- Types returned by the server ----

export interface Me {
  user: { id: string; displayName: string; isAdmin: boolean };
  person: { id: string; displayName: string; onboardingComplete: boolean };
}

export type Answer = "yes" | "no" | "not_sure";

export interface OnboardingState {
  step: string;
  completedAt: string | null;
  startYear: string | null;
  yearChoices: { fy: string; label: string }[];
  about: { displayName: string; dateOfBirth: string | null; residency: string | null; occupation: string | null };
  situation: {
    spouse: { answer: Answer | null };
    dependants: { answer: Answer | null; count: number | null };
    privateHospitalCover: { answer: Answer | null };
    studyLoan: { answer: Answer | null };
    medicareExemption: { answer: Answer | null };
  };
  areas: string[];
  areaChoices: { code: string; title: string; description: string }[];
  accountant: { name: string; firm: string | null; email: string | null } | null;
}

export interface Home {
  name: string;
  areas: string[];
  vehicles: number;
  properties: number;
  jobs: number;
  documents: { fy: string; label: string; have: number; total: number } | null;
  year: { fy: string; label: string };
  toReview: number;
  tasks: { id: string; title: string; dueOn: string | null }[];
  setupCards: { area: string; title: string; description: string; status: "coming_soon" | "todo" | "done"; action: string | null; doneText: string | null }[];
}

export type CarMethod = "logbook" | "cents_per_km";

export interface VehicleSummary {
  id: string;
  name: string;
  make: string | null;
  model: string | null;
  year: number | null;
  registration: string | null;
  acquiredOn: string;
  disposedOn: string | null;
  businessPct: number | null;
  methodThisYear: CarMethod | null;
}

export interface CarYear {
  fy: string;
  label: string;
  method: CarMethod;
  methodChosen: boolean;
  businessPct: number | null;
  businessKm: number | null;
  daysHeld: number;
  runningCosts: number;
  receiptCount: number;
  decline: { opening: number; decline: number; closing: number; explanation: string } | null;
  claim: { cents: number; explanation: string } | null;
  checks: { level: "warning" | "info"; message: string }[];
}

export type BalancingAdjustment =
  | { status: "none"; explanation: string }
  | { status: "refer"; explanation: string; difference: number }
  | { status: "calculated"; amount: number; kind: "assessable" | "deductible"; explanation: string };

export interface VehicleDetail {
  id: string;
  name: string;
  make: string | null;
  model: string | null;
  year: number | null;
  registration: string | null;
  finance: string;
  acquiredOn: string;
  costCents: number;
  odometerAtPurchase: number | null;
  depreciation: {
    method: "diminishing_value" | "prime_cost";
    effectiveLifeYears: number;
    depreciableCost: number | null;
    carLimit: { limit: number; fy: string; applied: boolean } | null;
    opening: { asAt: string; valueCents: number } | null;
    problem: string | null;
  };
  businessUseHistory: { pct: number; from: string; to: string | null }[];
  logbooks: { id: string; startsOn: string; endsOn: string; odometerStart: number; odometerEnd: number; businessKm: number; businessPct: number }[];
  odometer: { readOn: string; reading: number }[];
  years: CarYear[];
  sale: { soldOn: string; priceCents: number; odometer: number | null; balancingAdjustment: BalancingAdjustment } | null;
  currentBusinessPct: number | null;
}

export type PropertyUse = "main_residence" | "rented" | "available" | "private" | "vacant";

export interface PropertySummary {
  id: string;
  name: string;
  address: string | null;
  kind: string;
  settledOn: string;
  soldOn: string | null;
  currentUse: PropertyUse | null;
  yourSharePct: number;
}

export interface Owner {
  you: boolean;
  name: string | null;
  pct: number;
  from: string;
  to: string | null;
}

export interface RentalYear {
  fy: string;
  label: string;
  daysHeld: number;
  incomeDays: number;
  sharePct: number;
  grossRent: number;
  interest: number;
  capitalWorks: number;
  other: number;
  net: number;
  labels: { P: number; Q: number; F: number; U: number; net: number } | null;
  lines: { group: "income" | "interest" | "capital_works" | "other" | "not_deductible"; label: string; cents: number; explanation: string }[];
  checks: { level: "warning" | "info"; message: string }[];
}

export interface CapitalGain {
  status: "exempt" | "calculated" | "estimate";
  costBase: number;
  proceeds: number;
  gross: number;
  gain: number;
  loss: number;
  discountEligible: boolean;
  discountedGain: number;
  eventFy: string;
  steps: string[];
}

export interface PropertyDetail {
  id: string;
  name: string;
  address: string | null;
  state: string | null;
  postcode: string | null;
  kind: string;
  contractOn: string;
  settledOn: string;
  purchasePriceCents: number;
  firstIncomeUse: { on: string; valueCents: number } | null;
  absenceChoice: boolean;
  owners: Owner[];
  yourSharePct: number;
  useHistory: { use: PropertyUse; text: string; from: string; to: string | null }[];
  currentUse: PropertyUse | null;
  capitalWorks: { id: string; description: string; constructionStartedOn: string; completedOn: string; costCents: number; claimedBeforeCents: number; rateOverride: string | null; claimedToDateCents: number }[];
  costBase: { id: string; element: number; description: string; amountCents: number; incurredOn: string; saleCost: boolean }[];
  loans: { id: string; name: string; lender: string | null; closedOn: string | null; purposePct: number }[];
  years: RentalYear[];
  plant: { id: string; description: string; acquiredOn: string; costCents: number; effectiveLifeYears: number; method: string; secondHand: boolean; opening: { asAt: string; valueCents: number } | null; disposedOn: string | null; closingCents: number | null; blocked: boolean; problem: string | null }[];
  sale: { contractOn: string; settledOn: string; priceCents: number; capitalGain: CapitalGain | null; problem: string | null } | null;
}

export interface SettlementLine {
  label: string;
  cents: number;
  proposal: "cost_base" | "rental_expense" | "borrowing_cost" | "sale_cost" | "ignore";
  categoryCode?: string;
  element?: 2 | 4 | 5;
  why: string;
  line: string;
}

export type Reading =
  | { kind: "settlement"; reading: { kind: "purchase" | "sale" | "unknown"; priceCents: number | null; settlementDate: string | null; lines: SettlementLine[] } }
  | { kind: "schedule"; reading: { capitalWorks: { description: string; costCents: number; constructionStartedOn: string | null; completedOn: string | null } | null; plant: { description: string; costCents: number; effectiveLifeYears: number; method: "diminishing_value" | "prime_cost"; line: string }[]; notes: string[] } };

export interface Job {
  id: string;
  employer: string;
  abn: string | null;
  occupation: string | null;
  startedOn: string;
  endedOn: string | null;
  statements: { fy: string; label: string; grossCents: number; taxWithheldCents: number; allowancesCents: number; rfbaCents: number; rescCents: number; taxReady: boolean }[];
}

export interface WorkYear {
  fy: string;
  label: string;
  salary: { jobId: string; employer: string; grossCents: number | null; taxWithheldCents: number | null; allowancesCents: number | null; taxReady: boolean }[];
  homeOffice: { method: "fixed_rate" | "actual" | null; hours: number | null; loggedHours: number; loggedDays: number; workPct: number | null; claimCents: number | null; explanation: string | null };
  assets: { id: string; description: string; claimCents: number; workPct: number; explanation: string }[];
  receipts: { code: string; name: string; cents: number; count: number; excluded: boolean }[];
  d5Cents: number;
  checks: { level: "warning" | "info"; message: string }[];
}

export interface WorkAsset {
  id: string;
  kind: "work" | "property";
  description: string;
  acquiredOn: string;
  costCents: number;
  method: string;
  effectiveLifeYears: number;
  currentWorkPct: number | null;
  years: { fy: string; label: string; decline: number; closing: number; workPct: number; claim: number; explanation: string }[];
  sale: { disposedOn: string; proceedsCents: number; balancingAdjustment: BalancingAdjustment } | null;
  problem: string | null;
}

export interface Checklist {
  fy: string;
  label: string;
  yearOver: boolean;
  have: number;
  total: number;
  items: { key: string; title: string; about: string; where: string; status: "have" | "missing" | "not_yet" | "not_needed"; evidence: string | null; link: string | null; usual: boolean }[];
}

export interface LoanPurpose {
  type: "property" | "shares" | "private" | "other";
  id?: string;
  pct: number;
  name?: string;
}

export interface LoanSummary {
  id: string;
  name: string;
  lender: string | null;
  startedOn: string;
  closedOn: string | null;
  purposes: LoanPurpose[];
}

export interface LoanDetail {
  id: string;
  name: string;
  lender: string | null;
  accountHint: string | null;
  startedOn: string;
  amountCents: number;
  termYears: number | null;
  interestOnly: boolean;
  closedOn: string | null;
  owners: Owner[];
  purposeHistory: { from: string; to: string | null; purposes: LoanPurpose[] }[];
  statements: { id: string; from: string; to: string; interestCents: number; feesCents: number; closingBalanceCents: number | null }[];
  borrowingCosts: { id: string; description: string; amountCents: number; incurredOn: string }[];
  borrowingSchedule: { fy: string; label: string; deductionCents: number; remainingCents: number; explanation: string }[];
  problem: string | null;
  warnings: string[];
}

export interface Category {
  code: string;
  name: string;
  area: string;
  kind: "expense" | "income";
  help: string | null;
}

export type Confidence = "high" | "medium" | "low";

export interface Allocation {
  targetType: "work" | "private" | "rental" | "vehicle" | "investment" | "asset" | "other";
  targetId?: string | null;
  label?: string | null;
  share: number;
  claimPct: number;
}

export interface EntrySummary {
  id: string;
  kind: "expense" | "income";
  status: "draft" | "confirmed";
  date: string | null;
  amountCents: number | null;
  gstCents: number | null;
  supplier: string | null;
  abn: string | null;
  description: string | null;
  categoryCode: string | null;
  categoryName: string | null;
  autoConfirmed: boolean;
  documentId: string | null;
}

export interface EntryDetail extends EntrySummary {
  allocations: Allocation[];
  document: { id: string; mime: string; originalName: string | null; bytes: number; url: string } | null;
  read: { engine: string; fields: Record<string, { value: unknown; confidence: Confidence }> } | null;
  supplierRule: { name: string; timesConfirmed: number; autoConfirm: boolean; categoryCode: string } | null;
}

export function money(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return "—";
  return (cents / 100).toLocaleString("en-AU", { style: "currency", currency: "AUD" });
}

export function shortDate(iso: string | null): string {
  if (!iso) return "No date";
  return new Date(`${iso}T00:00:00`).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
}
